import { useState } from "react";
import { Plus } from "lucide-react";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { RowActions } from "@/components/row-actions";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { ApiError } from "@/lib/api";
import {
  useDatasheetTypes,
  useDeleteDatasheetType,
  type DatasheetType,
} from "@/lib/datasheets";
import { DatasheetTypeDialog } from "./DatasheetTypeDialog";

// The lookup is small and unpaginated server-side; DataGrid still requires page
// props, so we page client-side over the full result with a page size large
// enough that a single page always covers it.
const PAGE_SIZE = 200;

// Create, edit and delete all sit behind the single write claim the backend
// enforces on PUT and DELETE — there is no separate delete claim.
const WRITE_CLAIM = "datasheet-types:write";

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

export function DatasheetTypesPage() {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useDatasheetTypes();
  const rows = data ?? [];

  const { has } = usePermissions();
  const canWrite = has(WRITE_CLAIM);
  const confirm = useConfirm();
  const del = useDeleteDatasheetType();

  const [editing, setEditing] = useState<DatasheetType | null>(null);
  const [creating, setCreating] = useState(false);

  async function handleDelete(row: DatasheetType) {
    const ok = await confirm({
      title: "Delete datasheet type?",
      body: `${row.datasheetTypeCode} — ${row.description}`,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (!ok) return;
    del.mutate(row.datasheetTypeCode, {
      onSuccess: () => toast.success("Type deleted"),
      onError: (err) =>
        toast.error(serverMessage(err, "Could not delete the type")),
    });
  }

  const cols: DataGridColumn<DatasheetType>[] = [
    { id: "code", header: "Code", accessor: (r) => r.datasheetTypeCode },
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "product", header: "Product type", accessor: (r) => r.productTypeCode ?? "—" },
    { id: "status", header: "Default job status", accessor: (r) => r.defaultJobStatusCode ?? "—" },
    { id: "queue", header: "Work queue", accessor: (r) => r.addOrderToWorkQueueCode ?? "—" },
  ];

  if (canWrite) {
    cols.push({
      id: "actions",
      header: "",
      align: "right",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            label={r.datasheetTypeCode}
            onEdit={() => setEditing(r)}
            onDelete={() => void handleDelete(r)}
            deleteDisabled={del.isPending}
          />
        </div>
      ),
    });
  }

  const dialogOpen = creating || editing !== null;

  return (
    <ListPage
      title="Datasheet Types"
      backTo="/admin"
      primaryAction={
        canWrite ? (
          <IntentButton onClick={() => setCreating(true)} aria-label="Add datasheet type">
            <Plus className="mr-2 h-4 w-4" /> New type
          </IntentButton>
        ) : undefined
      }
    >
      <DataGrid
        columns={cols}
        rows={rows}
        rowKey={(r) => r.datasheetTypeCode}
        totalRows={rows.length}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        emptyHeadline="No datasheet types — add one."
        onPageChange={setPage}
      />

      {dialogOpen && (
        <DatasheetTypeDialog
          open={dialogOpen}
          onOpenChange={(o) => {
            if (!o) {
              setEditing(null);
              setCreating(false);
            }
          }}
          existing={editing}
        />
      )}
    </ListPage>
  );
}
